import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { SITE } from "@/lib/site";
import WhatsAppButton from "@/components/WhatsAppButton";

const NAV = [
  { label: "Inicio", to: "/", type: "route", testId: "nav-home" },
  { label: "Ejemplos", to: "/ejemplos", type: "route", testId: "nav-examples" },
  { label: "Paquetes", to: "/paquetes", type: "route", testId: "nav-packages" },
  { label: "Por qué elegirnos", hash: "porque", type: "anchor", testId: "nav-why" },
  { label: "Cómo trabajamos", hash: "como", type: "anchor", testId: "nav-how" },
  { label: "Contáctanos", to: "/contacto", type: "route", testId: "nav-contact" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Smooth scroll to anchor: navigate to /#hash if not on home
  const handleAnchor = (e, hash) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") {
      window.location.href = `/#${hash}`;
      return;
    }
    const el = document.getElementById(hash);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      history.replaceState(null, "", `#${hash}`);
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-black/85 backdrop-blur-xl border-b border-white/10"
          : "bg-black/40 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-24 md:h-28 flex items-center justify-between">
        <Link
          to="/"
          data-testid="logo-link"
          aria-label="Rincón Web — Inicio"
          className="flex items-center gap-2 group"
        >
          <img
            src={SITE.logoUrl}
            alt="Rincón Web"
            className="h-[68px] md:h-[82px] w-auto object-contain"
            loading="eager"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((item) =>
            item.type === "route" ? (
              <NavLink
                key={item.label}
                to={item.to}
                data-testid={item.testId}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-colors duration-200 hover:text-[#D4AF37] ${
                    isActive ? "text-[#D4AF37]" : "text-white/90"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ) : (
              <a
                key={item.label}
                href={`/#${item.hash}`}
                data-testid={item.testId}
                onClick={(e) => handleAnchor(e, item.hash)}
                className="text-sm tracking-wide text-white/90 transition-colors duration-200 hover:text-[#D4AF37]"
              >
                {item.label}
              </a>
            )
          )}
        </nav>

        <div className="hidden lg:block">
          <WhatsAppButton
            testId="header-wa-btn"
            label="WhatsApp"
            variant="primary"
            className="text-sm px-5 py-2.5"
          />
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((s) => !s)}
          aria-label="Abrir menú"
          className="lg:hidden text-white p-2"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl"
        >
          <nav className="px-6 py-6 flex flex-col gap-4">
            {NAV.map((item) =>
              item.type === "route" ? (
                <NavLink
                  key={item.label}
                  to={item.to}
                  data-testid={`m-${item.testId}`}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `text-base ${isActive ? "text-[#D4AF37]" : "text-white"}`
                  }
                >
                  {item.label}
                </NavLink>
              ) : (
                <a
                  key={item.label}
                  href={`/#${item.hash}`}
                  data-testid={`m-${item.testId}`}
                  onClick={(e) => handleAnchor(e, item.hash)}
                  className="text-base text-white hover:text-[#D4AF37]"
                >
                  {item.label}
                </a>
              )
            )}
            <WhatsAppButton
              testId="m-header-wa-btn"
              label={`WhatsApp ${SITE.whatsappDisplay}`}
              variant="primary"
              className="mt-2"
            />
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
